import { useEffect } from 'react'
import { Grid, Layers, X, Menu, AlertTriangle } from 'lucide-react'
import SidebarButtonItem from '../components/sidebar-button-item'
import { SwitchSidebarPanel } from '../components/sidebar-switch-panel'
import { useFlowStore } from '@/stores/flow-store'

type DesktopSidebarFragmentProps = Readonly<{
  activePanel: 'node-properties' | 'available-nodes' | 'validation-errors' | 'none'
  setActivePanel: (
    panel: 'node-properties' | 'available-nodes' | 'validation-errors' | 'none'
  ) => void
  isSidebarOpen: boolean
  setSidebarOpen: (isOpen: boolean) => void
}>

export function DesktopSidebarFragment({
  activePanel,
  setActivePanel,
  isSidebarOpen,
  setSidebarOpen,
}: DesktopSidebarFragmentProps) {
  const { actions: { validation: validationActions } } = useFlowStore();
  const errorCount = validationActions.getErrorCount();

  useEffect(() => {
    if (activePanel === 'none' && isSidebarOpen) {
      setActivePanel('available-nodes')
    }
  }, [activePanel, setActivePanel, isSidebarOpen])

  return (
    <div className='relative flex h-full w-fit max-w-sm shrink-0 divide-x divide-card-foreground/10'>
      <div className='shrink-0 bg-card p-1.5'>
        <div className='flex h-full flex-col gap-2'>
          <SidebarButtonItem onClick={() => setSidebarOpen(!isSidebarOpen)}>
            {isSidebarOpen ? (
              <X className='size-5' />
            ) : (
              <Menu className='size-5' />
            )}
          </SidebarButtonItem>

          <div className='mx-auto h-px w-4 bg-card-foreground/10' />

          <SidebarButtonItem
            active={activePanel === 'available-nodes'}
            onClick={() => {
              setActivePanel('available-nodes')
              if (!isSidebarOpen) {
                setSidebarOpen(true)
              }
            }}
          >
            <Grid className='size-5' />
          </SidebarButtonItem>

          <SidebarButtonItem
            active={activePanel === 'node-properties'}
            onClick={() => {
              setActivePanel('node-properties')
              if (!isSidebarOpen) {
                setSidebarOpen(true)
              }
            }}
          >
            <Layers className='size-5' />
          </SidebarButtonItem>

          <div className='relative'>
            <SidebarButtonItem
              active={activePanel === 'validation-errors'}
              onClick={() => {
                setActivePanel('validation-errors')
                if (!isSidebarOpen) {
                  setSidebarOpen(true)
                }
              }}
            >
              <AlertTriangle className='size-5' />
            </SidebarButtonItem>
            {errorCount > 0 && (
              <div className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full px-1">
                {errorCount > 99 ? '99+' : errorCount}
              </div>
            )}
          </div>
        </div>
      </div>

      {activePanel !== 'none' && isSidebarOpen && (
        <div className='min-w-xs flex h-full min-h-0 grow flex-col bg-card'>
          <SwitchSidebarPanel active={activePanel} />
        </div>
      )}
    </div>
  )
}
